/**
 * Forward + reverse BFS over a Sankey link list.
 *
 * The data-flow Sankey hovers a node and dims every edge that isn't
 * part of the connected flow. This file is the testable seam for that
 * traversal so the rendering layer stays a thin SVG shell.
 *
 * Pure: no DOM, no d3.
 */

export function computeHighlightedFlow(
  activeNodeId: string,
  links: ReadonlyArray<{ sourceId: string; targetId: string }>,
): { nodes: Set<string>; links: Set<number> } {
  const outAdj = new Map<string, number[]>();
  const inAdj = new Map<string, number[]>();
  links.forEach((l, idx) => {
    let outs = outAdj.get(l.sourceId);
    if (!outs) { outs = []; outAdj.set(l.sourceId, outs); }
    outs.push(idx);
    let ins = inAdj.get(l.targetId);
    if (!ins) { ins = []; inAdj.set(l.targetId, ins); }
    ins.push(idx);
  });

  const nodes = new Set<string>([activeNodeId]);
  const linkSet = new Set<number>();

  const walk = (
    adj: Map<string, number[]>,
    nextOf: (link: { sourceId: string; targetId: string }) => string,
  ) => {
    const q: string[] = [activeNodeId];
    while (q.length > 0) {
      const cur = q.shift()!;
      for (const idx of adj.get(cur) ?? []) {
        if (linkSet.has(idx)) continue;
        linkSet.add(idx);
        const next = nextOf(links[idx]!);
        if (!nodes.has(next)) {
          nodes.add(next);
          q.push(next);
        }
      }
    }
  };

  walk(outAdj, (l) => l.targetId);
  walk(inAdj, (l) => l.sourceId);

  return { nodes, links: linkSet };
}
